import React, {
  useCallback, useEffect, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import ForceGuaranteePageView from '../views/ForceGuarantee/Index';
import {
  useForceGuaranteeProducts,
  useForceGuaranteeTransactions,
  useForceGuaranteeEvaluation,
  useForceGuaranteeResult,
  useForceGuaranteeDerived,
  useForceGuaranteeActions,
} from '../hooks/useForceGuarantee';
import useGraphQLQuery from '../hooks/useGraphQLQuery';
import ruleEvaluationOrdersQuery from '../graphql/ruleEvaluationOrdersQuery';
import {
  parseForceGuaranteeContext,
  findOrderIdByExternalId,
  isSameOrderId,
} from '../utilities/forceGuarantee';
import { TRANSACTION_NOT_MATCHED_MESSAGE } from '../constants/forceGuarantee';
import { PRODUCT_FAMILY_DISPLAY_NAMES } from '../constants/productFamily';

// Owns the flow; a launch context in the query string locks the product, looks up and pre-selects the row.
const ForceGuarantee = ({ location }) => {
  const { products, productsLoading, productsErrorMessage } = useForceGuaranteeProducts();
  const {
    transactions,
    transactionsLoading,
    transactionsErrorMessage,
    selectedId,
    search,
  } = useForceGuaranteeTransactions();
  const {
    evaluatedId,
    eligibilityResults,
    evaluationLoading,
    evaluationErrorMessage,
  } = useForceGuaranteeEvaluation();
  const {
    forceResult, forceLoading, forceErrorMessage, modalOpen,
  } = useForceGuaranteeResult();
  const {
    busy,
    canForce,
    hasSearched,
    failedRulesCount,
    totalRulesCount,
    evaluatedTransaction,
    selectedProduct,
  } = useForceGuaranteeDerived();
  const {
    loadProducts,
    searchTransactions,
    selectTransaction,
    runEligibility,
    openModal,
    closeModal,
    confirmForce,
    backToTransactions,
    resetFlow,
  } = useForceGuaranteeActions();

  const locationSearch = (location && location.search) || '';
  const context = useMemo(
    () => parseForceGuaranteeContext(locationSearch),
    [locationSearch]
  );
  // A context is only actionable with both a forceable product and a well-formed VIN.
  const hasContext = !!(context.productCode && context.vin);

  // The only query exposing both identifiers, so it translates the row's `externalId` to `orderId`.
  const {
    data: translationData,
    loading: translationLoading,
  } = useGraphQLQuery({
    query: ruleEvaluationOrdersQuery,
    variables: { vin: context.vin },
    skip: !hasContext || !context.externalId,
  });

  const resolvedOrderId = useMemo(
    () => findOrderIdByExternalId(
      translationData && translationData.ruleEvaluationOrders,
      context.externalId
    ),
    [translationData, context.externalId]
  );

  const matchedTransaction = useMemo(
    () => (resolvedOrderId
      ? transactions.find(txn => isSameOrderId(txn.id, resolvedOrderId))
      : undefined),
    [transactions, resolvedOrderId]
  );

  const contextLookupSettled = !transactionsLoading
    && search.vin === context.vin
    && search.productCode === context.productCode;
  const translationSettled = !context.externalId || !translationLoading;

  // A flag rather than `selectedId`, which stays null when the pre-selection finds no match.
  const bootstrap = useRef({ search: null, selectFired: false });
  if (bootstrap.current.search !== locationSearch) {
    bootstrap.current = { search: locationSearch, selectFired: false };
  }

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // The slice lives in the global store, so a revisit would show the previous VIN's results.
  useEffect(() => resetFlow, [resetFlow]);

  // FR-010: with a context the lookup needs no operator input.
  useEffect(() => {
    if (!hasContext) return;
    searchTransactions(context.productCode, context.vin);
  }, [hasContext, context, searchTransactions]);

  // FR-011/FR-012: pre-select the row's transaction once both the lookup and the translation settle.
  useEffect(() => {
    if (!hasContext || bootstrap.current.selectFired) return;
    if (!contextLookupSettled || !translationSettled) return;
    bootstrap.current.selectFired = true;
    if (!matchedTransaction) return;
    // The operator can select first while the translation is in flight; their choice outranks the row's.
    if (selectedId !== null) return;
    selectTransaction(matchedTransaction.id);
  }, [
    hasContext,
    contextLookupSettled,
    translationSettled,
    matchedTransaction,
    selectedId,
    selectTransaction,
  ]);

  // Shown only with an originating identifier and a non-empty list, so the notice points somewhere real.
  const transactionNotMatchedMessage = hasContext
    && !!context.externalId
    && contextLookupSettled
    && translationSettled
    && transactions.length > 0
    && !matchedTransaction
    ? TRANSACTION_NOT_MATCHED_MESSAGE
    : null;

  // From the frontend constant: the catalogue is gated by another permission and is empty here.
  const productLabel = hasContext
    ? PRODUCT_FAMILY_DISPLAY_NAMES[context.productCode]
    : null;

  const handleRunEligibility = useCallback(
    () => runEligibility(selectedId),
    [runEligibility, selectedId]
  );

  // The force targets the evaluated order, not the selected row; `selectCanForce` gates the button.
  const handleConfirm = useCallback(
    justification => confirmForce(evaluatedId, justification),
    [confirmForce, evaluatedId]
  );

  return (
    <ForceGuaranteePageView
      products={products}
      productsLoading={productsLoading}
      productsErrorMessage={productsErrorMessage}
      transactions={transactions}
      transactionsLoading={transactionsLoading}
      transactionsErrorMessage={transactionsErrorMessage}
      selectedId={selectedId}
      productCode={search.productCode || (hasContext ? context.productCode : '')}
      vin={search.vin}
      hasSearched={hasSearched}
      evaluatedId={evaluatedId}
      eligibilityResults={eligibilityResults}
      evaluationLoading={evaluationLoading}
      evaluationErrorMessage={evaluationErrorMessage}
      evaluatedVin={evaluatedTransaction ? evaluatedTransaction.vin : search.vin}
      productName={
        productLabel || (selectedProduct ? selectedProduct.displayName : '')
      }
      failedRulesCount={failedRulesCount}
      totalRulesCount={totalRulesCount}
      forceResult={forceResult}
      forceLoading={forceLoading}
      forceErrorMessage={forceErrorMessage}
      modalOpen={modalOpen}
      busy={busy}
      canForce={canForce}
      productLocked={hasContext}
      productLabel={productLabel}
      transactionNotMatchedMessage={transactionNotMatchedMessage}
      onSearch={searchTransactions}
      onSelect={selectTransaction}
      onRunEligibility={handleRunEligibility}
      onOpenModal={openModal}
      onCloseModal={closeModal}
      onConfirm={handleConfirm}
      onBack={backToTransactions}
      onReset={resetFlow}
    />
  );
};

ForceGuarantee.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
};

ForceGuarantee.defaultProps = {
  location: null,
};

export default ForceGuarantee;
